if (!customElements.get('mixed-styles-card-product')) {
  class MixedStylesCardProduct extends HTMLElement {
    // Static property to persist across instances
    static imagesRequests = new Set();
    constructor() {
      super();
      this.connectedCallback = this.connectedCallback.bind(this);
      this.disconnectedCallback = this.disconnectedCallback.bind(this);
    }

    /**
     * Fetches <numberOfImages> images for the given parentSku/colorCode/partnerUuid combo.
     *
     * @param {*} parentSku
     * @param {*} partnerUuid
     * @param {*} colorCode
     * @param {*} numberOfImages
     * @returns a list of images
     */
    async fetchInventoryImages({ parentSku, partnerUuid, colorCode, numberOfImages }) {
      const query = `
        query inventoryImages(
          $partnerUuid: String!
          $parentSku: String!
          $colorCode: String
          $numberOfImages: Int
        ) {
          inventoryImages(
            partnerUuid: $partnerUuid,
            parentSku: $parentSku,
            colorCode: $colorCode,
            numberOfImages: $numberOfImages,
          ) {
            tag
            url
          }
        }
      `;
      const variables = { partnerUuid, parentSku, colorCode, numberOfImages };
      const param = 'query-inventoryImages';

      // Check if the request has been made already to avoid refetching and performance problems
      if (MixedStylesCardProduct.imagesRequests.has(JSON.stringify({ variables }))) {
        return null;
      } 
      MixedStylesCardProduct.imagesRequests.add(JSON.stringify({ variables })); 

      try {
        const response = await fetchWithAppName(`${global.url}${param}`, {
          body: JSON.stringify({ query, variables }),
        });
        const jsonData = await response.json();
        const { inventoryImages } = jsonData.data;
        return inventoryImages;
      } catch (e) {
        console.error(e);
        return null;
      }
    }

    /**
     * Initializes settings based on custom attributes when the element is connected to the DOM.
     * @returns {void}
     */
    connectedCallback() {
      const parentSku = this.getAttribute('parent-sku');
      const colorCode = this.getAttribute('color-code');
      const layout = this.getAttribute('layout');
      const width = this.getAttribute('width');
      const regex = /^(?<rows>\d+)x(?<columns>\d+)$/;
      const match = layout.match(regex);
      const rows = match.groups.rows;
      const columns = match.groups.columns;
      const area = rows * columns;
      const fallbackWidthOnFail = this.getAttribute('fallback-width-on-fail')?.toLowerCase() === 'true';

      this.fetchInventoryImages({
        parentSku,
        partnerUuid: global.partnerUuid,
        colorCode,
        numberOfImages: area,
      })
        .then((inventoryImages) => {
          if (!inventoryImages) return;
          const container = this.querySelector('.container');
          const effectiveColumns = (inventoryImages.length === area) ? columns : 1;
          const createImageAndAppend = (imageUrl) => {
            const imageElement = document.createElement("img");
            imageElement.src = normalizeUrl(imageUrl, { width: width ? width/effectiveColumns : '300', height: width ? width/effectiveColumns : '300' });
            imageElement.alt = 'open sku';
            imageElement.setAttribute('onerror', `this.onerror=null; this.src='${localStorage.getItem('cameraShyImageUrl')}'`);
            container.append(imageElement);
          };
          
          if (inventoryImages.length === area) {
            inventoryImages.forEach(image => {
              createImageAndAppend(image.url);
            });
          } else if (inventoryImages.length >= 1) {
            createImageAndAppend(inventoryImages[0].url);
          } else {
            const cameraShyImageUrl = localStorage.getItem('cameraShyImageUrl');
            const shouldHaveWidth = fallbackWidthOnFail && width;
            const fallbackImageUrl = shouldHaveWidth ? `${cameraShyImageUrl}&width=${width/effectiveColumns}` : cameraShyImageUrl;
            createImageAndAppend(fallbackImageUrl);
          }
          container.style.setProperty('--number-of-columns', effectiveColumns);
        })
        .catch((error) => {
          console.error('Error:', error);
        });
    }

    /**
     * Callback invoked when the element is disconnected from the document's DOM.
     * Clears any content added to the element's container via append, ensuring a clean state if connectedCallback is called again.
     * @returns {void}
     */
    disconnectedCallback() {
      const container = this.querySelector('.container');
      if (!container) return;
      container.innerHTML = '';
    }
  }

  customElements.define('mixed-styles-card-product', MixedStylesCardProduct);
}
